import React from 'react';

type VariableValue = Exclude<React.ReactNode, boolean> | string;

type Variables<Key extends string> = Record<Key, VariableValue>;

export const formatStringMessage = <VariableKey extends string>(
	message: string,
	variables?: Record<VariableKey, string>,
): string => formatMessage(message, variables).filter(assertString).join('');

export const formatReactMessage = <VariableKey extends string>(
	message: string,
	variables?: Record<VariableKey, VariableValue>,
): JSX.Element => (
	<>
		{formatMessage(message, variables).map((e, i) => (
			<React.Fragment key={i}>{e}</React.Fragment>
		))}
	</>
);

export const formatMessage = <VariableKey extends string>(
	message: string,
	variables?: Record<VariableKey, VariableValue>,
): VariableValue[] =>
	message
		.split(/({[A-z]*})/g)
		.map(getVariableValues(variables))
		.reduce(concatStringsReducer, []);

const getVariableValues =
	<Key extends string>(variables: Variables<Key> | undefined) =>
	(input: string): VariableValue => {
		const variableName = input.slice(1, -1) as Key;

		const variableValue = variables?.[variableName];

		return variableValue === undefined ? input : variableValue;
	};

const concatStringsReducer = (
	accumulator: VariableValue[],
	current: VariableValue,
): VariableValue[] => {
	const [previous, ...rest] = [...accumulator].reverse();

	if (typeof current === 'string' && typeof previous === 'string') {
		return [`${previous}${current}`, ...rest].reverse();
	}

	return [...accumulator, current];
};

const assertString = (s: unknown): s is string => typeof s === 'string';
